import React, { useRef, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = /\.(pdf|jpe?g|png)$/i;
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const validateProof = (file, intl) => {
  if (!file) return null;
  if (!ACCEPTED_EXTENSIONS.test(file.name) || (file.type && !ACCEPTED_TYPES.includes(file.type))) {
    return intl.formatMessage({ id: "gestion.playerFinance.proofInvalidType" });
  }
  if (file.size > MAX_SIZE) {
    return intl.formatMessage({ id: "gestion.playerFinance.proofTooLarge" });
  }
  return null;
};

const PaymentProofDropzone = ({ file, onChange, disabled = false, error }) => {
  const intl = useIntl();
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState(null);

  const selectFile = (nextFile) => {
    const validationError = validateProof(nextFile, intl);
    setLocalError(validationError);
    onChange(validationError ? null : nextFile);
  };

  return (
    <Stack spacing={1}>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) inputRef.current?.click();
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!disabled) selectFile(event.dataTransfer.files?.[0] || null);
        }}
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: error || localError ? "error.main" : "divider",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={inputRef}
          hidden
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          disabled={disabled}
          onChange={(event) => selectFile(event.target.files?.[0] || null)}
        />
        <RevaText sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: "gestion.playerFinance.proofRequired" })}
        </RevaText>
        <RevaText variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "gestion.playerFinance.proofHint" })}
        </RevaText>
        {file && <RevaText sx={{ mt: 1 }}>{file.name}</RevaText>}
      </Box>
      {file && (
        <Button size="small" color="error" onClick={() => onChange(null)} disabled={disabled} sx={{ alignSelf: "start" }}>
          {intl.formatMessage({ id: "gestion.playerFinance.removeProof" })}
        </Button>
      )}
      {(error || localError) && <RevaText color="error">{error || localError}</RevaText>}
    </Stack>
  );
};

export default PaymentProofDropzone;
